import React from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Wrench,
  Clock,
  ShieldCheck,
  Phone,
  ClipboardCheck,
  HeartPulse,
  BarChart2,
  AlertCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import img from "../../Images/maintain.avif"
import img1 from "../../Images/main1.jpeg"
const MaintenanceServicePage = () => {
  const navigate = useNavigate();
  
  const handleEmergencyCall = () => {
    window.location.href = 'tel:+9779802781400';
  };

  const services = [
    {
      title: "Preventive Maintenance",
      description: "Scheduled maintenance programs designed to prevent breakdowns and extend equipment life through systematic inspections and servicing.",
      icon: <ShieldCheck className="w-8 h-8 text-teal-600" />
    },
    {
      title: "Emergency Repairs",
      description: "24/7 emergency response team for quick resolution of critical system failures and unexpected breakdowns.",
      icon: <AlertCircle className="w-8 h-8 text-teal-600" />
    },
    {
      title: "Complete Overhauling",
      description: "Comprehensive system overhaul services including dismantling, inspection, repairs, and reassembly with quality assurance.",
      icon: <Wrench className="w-8 h-8 text-teal-600" />
    }
  ];

  const benefits = [
    {
      title: "Equipment Lifespan",
      description: "Extended equipment life through regular maintenance",
      icon: <HeartPulse className="w-6 h-6 text-teal-600" />,
      stat: "+40% Longer"
    },
    {
      title: "Downtime Reduction",
      description: "Minimized unexpected breakdowns and repairs",
      icon: <Clock className="w-6 h-6 text-teal-600" />,
      stat: "85% Less"
    },
    {
      title: "Performance",
      description: "Improved system efficiency and reliability",
      icon: <BarChart2 className="w-6 h-6 text-teal-600" />,
      stat: "95% Optimal"
    }
  ];

  const maintenanceProcess = [
    {
      title: "Inspection",
      description: "Thorough assessment of system condition and performance"
    },
    {
      title: "Planning",
      description: "Detailed maintenance schedule and resource allocation"
    },
    {
      title: "Execution",
      description: "Professional maintenance and repair implementation"
    },
    {
      title: "Quality Check",
      description: "Comprehensive testing and performance verification"
    }
  ];

  // SEO Structured Data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Industrial Maintenance and Overhauling Services",
    "provider": {
      "@type": "Organization",
      "name": "Gortherm Energy",
      "telephone": "+977-9802781400",
      "url": "https://www.gorthermenergy.com"
    },
    "areaServed": {
      "@type": "Country",
      "name": "Nepal"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Maintenance Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Preventive Maintenance",
            "description": "Scheduled maintenance programs to prevent breakdowns"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Emergency Repairs",
            "description": "24/7 emergency response for critical failures"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Complete Overhauling",
            "description": "Comprehensive system overhaul services"
          }
        }
      ]
    },
    "availableChannel": {
      "@type": "ServiceChannel",
      "servicePhone": {
        "@type": "ContactPoint",
        "telephone": "+977-9802781400",
        "contactType": "Emergency Service"
      }
    }
  };

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>Industrial Maintenance & Overhauling Services | Gortherm Energy</title>
        <meta 
          name="title" 
          content="Industrial Maintenance & Overhauling Services | Gortherm Energy" 
        />
        <meta 
          name="description" 
          content="Professional industrial maintenance, preventive maintenance, emergency repairs, and complete overhauling services. 24/7 emergency support. Extend equipment life by 40% with expert maintenance solutions." 
        />
        <meta 
          name="keywords" 
          content="industrial maintenance, preventive maintenance, emergency repairs, equipment overhauling, boiler maintenance, HVAC maintenance, 24/7 emergency service, industrial equipment repair, maintenance services Nepal, scheduled maintenance" 
        />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://www.gorthermenergy.com/services/maintenance" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.gorthermenergy.com/services/maintenance" />
        <meta property="og:title" content="Industrial Maintenance & Overhauling Services | Gortherm Energy" />
        <meta property="og:description" content="Professional industrial maintenance services with 24/7 emergency support. Preventive maintenance, emergency repairs, and complete overhauling solutions." />
        <meta property="og:image" content="https://www.gorthermenergy.com/images/maintenance-service.jpg" />
        <meta property="og:site_name" content="Gortherm Energy" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://www.gorthermenergy.com/services/maintenance" />
        <meta property="twitter:title" content="Industrial Maintenance & Overhauling Services | Gortherm Energy" />
        <meta property="twitter:description" content="Professional industrial maintenance services with 24/7 emergency support. Extend equipment life by 40%." />
        <meta property="twitter:image" content="https://www.gorthermenergy.com/images/maintenance-service.jpg" />

        {/* Additional SEO tags */}
        <meta name="author" content="Gortherm Energy" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />
        <meta name="geo.region" content="NP" />
        <meta name="geo.placename" content="Nepal" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <article className="w-full">
        {/* Hero Section - Mobile Responsive */}
        <header className="relative h-[500px] sm:h-[450px] md:h-96 bg-gradient-to-r from-teal-900 to-cyan-800 overflow-hidden pt-16 sm:pt-20 md:pt-16">
          <div className="absolute inset-0">
            <img
              src={img1}
              alt="Professional industrial maintenance services and equipment overhauling"
              className="w-full h-full object-cover opacity-20"
              loading="eager"
            />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
            <div className="text-white max-w-3xl">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4 leading-tight">
                Maintenance & Overhauling Services
              </h1>
              <p className="text-lg sm:text-xl md:text-2xl mb-6 sm:mb-8">
                Professional maintenance solutions to keep your industrial systems running at peak performance.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <button 
                  className="bg-teal-500 hover:bg-teal-600 text-white px-6 py-3 rounded-lg flex items-center justify-center gap-2 transition-all shadow-lg hover:shadow-xl"
                  onClick={() => navigate('/contact')}
                  aria-label="Schedule maintenance service"
                >
                  Schedule Service
                  <Clock className="w-5 h-5" />
                </button>
                <button
                  onClick={handleEmergencyCall}
                  className="bg-white hover:bg-gray-100 text-teal-900 px-6 py-3 rounded-lg flex items-center justify-center gap-2 transition-all shadow-lg hover:shadow-xl"
                  aria-label="Call emergency hotline"
                >
                  Emergency Call
                  <Phone className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          {/* Services Grid */}
          <section className="mb-12 sm:mb-16">
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">Our Maintenance Services</h2>
              <p className="text-gray-600 max-w-3xl mx-auto text-sm sm:text-base px-4">
                Comprehensive maintenance and overhauling solutions to ensure optimal 
                performance and longevity of your industrial equipment.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {services.map((service, index) => (
                <article 
                  key={index}
                  className="p-6 rounded-xl border border-gray-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white"
                >
                  <div className="mb-4" aria-hidden="true">
                    {service.icon}
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold mb-3">{service.title}</h3>
                  <p className="text-gray-600 text-sm sm:text-base">{service.description}</p>
                </article>
              ))}
            </div>
          </section>

          {/* Benefits Section */}
          <section className="bg-teal-50 rounded-xl p-6 sm:p-8 mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8 text-center">Service Benefits</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="text-center p-4 sm:p-6 bg-white rounded-lg shadow-sm">
                  <div className="flex justify-center mb-4" aria-hidden="true">
                    {benefit.icon}
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold mb-2">{benefit.title}</h3>
                  <p className="text-gray-600 mb-4 text-sm sm:text-base">{benefit.description}</p>
                  <div className="text-xl sm:text-2xl font-bold text-teal-600">{benefit.stat}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Maintenance Process */}
          <section className="mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold mb-8 sm:mb-12 text-center">Our Maintenance Process</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
              {maintenanceProcess.map((step, index) => (
                <div key={index} className="relative">
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mb-4 flex-shrink-0">
                      <span className="text-teal-600 font-bold text-lg">{index + 1}</span>
                    </div>
                    <h3 className="text-base sm:text-lg font-semibold mb-2 text-center">{step.title}</h3>
                    <p className="text-gray-600 text-center text-sm sm:text-base">{step.description}</p>
                  </div>
                  {index < maintenanceProcess.length - 1 && (
                    <div className="hidden lg:block absolute top-6 left-[60%] w-[80%] h-[2px] bg-teal-100" aria-hidden="true"></div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Expertise Section */}
          <section className="grid md:grid-cols-2 gap-8 sm:gap-12 items-center mb-12 sm:mb-16">
            <div className="relative h-64 sm:h-80 md:h-96 order-2 md:order-1">
              <img 
                src={img}
                alt="Professional maintenance technicians performing industrial equipment servicing" 
                className="rounded-lg shadow-xl object-cover h-full w-full"
                loading="lazy"
              />
            </div>
            <div className="order-1 md:order-2">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">Why Choose Our Maintenance Services</h2>
              <div className="space-y-4 sm:space-y-6">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="p-2 bg-teal-100 rounded-lg flex-shrink-0" aria-hidden="true">
                    <Wrench className="w-5 h-5 sm:w-6 sm:h-6 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2 text-base sm:text-lg">Expert Technicians</h3>
                    <p className="text-gray-600 text-sm sm:text-base">
                      Highly trained and certified maintenance professionals with years of industrial experience.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="p-2 bg-teal-100 rounded-lg flex-shrink-0" aria-hidden="true">
                    <ClipboardCheck className="w-5 h-5 sm:w-6 sm:h-6 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2 text-base sm:text-lg">Quality Assurance</h3>
                    <p className="text-gray-600 text-sm sm:text-base">
                      Rigorous quality control procedures ensuring reliable and lasting maintenance solutions.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Emergency Service Call-out */}
          <section className="bg-teal-50 rounded-xl p-6 sm:p-8">
            <div className="text-center">
              <h2 className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4">24/7 Emergency Service</h2>
              <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base max-w-2xl mx-auto">
                Our emergency response team is available round the clock for urgent maintenance needs.
              </p>
              <button
                onClick={handleEmergencyCall}
                className="bg-teal-600 hover:bg-teal-700 text-white px-6 sm:px-8 py-3 rounded-lg flex items-center gap-2 mx-auto transition-all shadow-lg hover:shadow-xl"
                aria-label="Call emergency hotline at +977-9802781400"
              >
                <span className="text-sm sm:text-base">Emergency Hotline: +977-9802781400</span>
                <Phone className="w-5 h-5" />
              </button>
            </div>
          </section>
        </main>
      </article>
    </>
  );
};

export default MaintenanceServicePage;